import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../models/UiUtils';
import { AUTOMATION_QUERY_KEYS } from '../../../../../queries/Automation/queryKeys';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { StepSendFromOption } from '../../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { HandwrittenCardTemplateSearchModal } from '../../../../components/HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { HandwrittenCardStepForm } from '../../components/HandwrittenCardStepForm';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { StepAdvancedOptionsPanel } from '../../components/StepAdvancedOptionsPanel';
import { useHandwrittenCardStep } from '../../hooks/useHandwrittenCardStep';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { rendererContainer } from './styles/commonRendererStyles';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { ImpersonationContextViewModel } from '../../../../../viewmodels/AdminViewModels';

interface HandwrittenCardStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function HandwrittenCardStepRenderer({
	nodeId,
	data,
	readOnly: readOnlyProp,
}: HandwrittenCardStepRendererProps) {
	const {
		step: hwcStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
	} = useStepRenderer<Api.HandwrittenCardAutomationStep>(data, readOnlyProp);

	const { updateNodeData, impersonationContext } = useAutomationFlowContext();
	const stepType = data.stepType;
	const schedule = hwcStep?.schedule || stepData?.schedule;
	const templateId = template?.id;
	const queryClient = useQueryClient();

	const impersonationContextViewModel = React.useMemo(
		() => (impersonationContext ? new ImpersonationContextViewModel(impersonationContext) : undefined),
		[impersonationContext]
	);

	const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);

	const {
		name,
		setName,
		selectedTemplateId,
		setSelectedTemplateId,
		contentEditorState,
		setContentEditorState,
		includeSignature,
		setIncludeSignature,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		skipStepContactFilter,
		setSkipStepContactFilter,
		autoSave,
		clearInlineErrorMessage,
		save,
		saveSenderOptions,
	} = useHandwrittenCardStep({
		hwcStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.HandwrittenCardAutomationStep>) => {
		if (!updateNodeData || !hwcStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...hwcStep, ...updatedFields },
		});
	};

	const handleNameChange = (newName: string) => {
		setName(newName);
		clearInlineErrorMessage();
	};

	const handleNameBlur = () => {
		save({ newName: name });
		syncStepToNodeData({ name });
	};

	const handleSelectTemplate = () => {
		setIsTemplateModalOpen(true);
		clearInlineErrorMessage();
	};

	const handleTemplateChosen = (selectedTemplate: Api.IHandwrittenCardTemplate) => {
		setSelectedTemplateId(selectedTemplate.id);

		const templateContent = convertRawRichTextContentStateToRichContentEditorState(selectedTemplate.content);
		const preview = contentEditorState.getPlainTextPreview();
		const shouldOverwriteContent = !preview || preview.length === 0;

		if (shouldOverwriteContent) {
			setContentEditorState(templateContent);
			save({
				newTemplateId: selectedTemplate.id,
				newContent: templateContent.getRawRichTextContent(),
			});
			syncStepToNodeData({
				content: templateContent.getRawRichTextContent(),
				templateReference: {
					templateId: selectedTemplate.id,
					isCustomized: false,
					isSystemTemplate: false,
				},
			});
		} else {
			save({ newTemplateId: selectedTemplate.id });
			syncStepToNodeData({
				templateReference: {
					templateId: selectedTemplate.id,
					isCustomized: false,
					isSystemTemplate: false,
				},
			});
		}

		setIsTemplateModalOpen(false);
	};

	const handleContentStateChange = (state: Api.IRichContentEditorState) => {
		setContentEditorState(state);
	};

	const handleContentBlur = () => {
		const rawContent = contentEditorState.getRawRichTextContent();
		save({ newContent: rawContent });
		syncStepToNodeData({ content: rawContent });
	};

	const handleIncludeSignatureChange = (include: boolean) => {
		setIncludeSignature(include);
		save({ newIncludeSignature: include });
		syncStepToNodeData({ excludeSignature: !include });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const handleSaveSenderOptions = (option: Api.ISendFromOptions | null, _sendToSender?: boolean) => {
		saveSenderOptions(option);
		syncStepToNodeData({ sendFromOptions: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	// Refresh template data after conditional step changes (add/remove fallback email)
	const handleStepChanged = React.useCallback(() => {
		if (templateId) {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
		}
	}, [queryClient, templateId]);

	// Callback for the form to push updated step data back to the canvas node.
	// Matches the legacy list-editor pattern (HwcAutomationStepCardContent.updateStep).
	const handleUpdateStep = React.useCallback(
		(_stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => {
			if (!updateNodeData || !hwcStep) return;
			const updated = updater(hwcStep as Api.IAutomationStep);
			updateNodeData(nodeId, { automationStep: updated });
		},
		[updateNodeData, nodeId, hwcStep]
	);

	const baseKey = `${stepType}-${nodeId}-${hwcStep?.id || ''}`;

	return (
		<div className={css(rendererContainer)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{hwcStep && trigger && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={hwcStep}
					index={0}
					onScheduleChange={handleScheduleChange}
				/>
			)}
			<HandwrittenCardStepForm
				name={name}
				selectedTemplateId={selectedTemplateId}
				contentEditorState={contentEditorState}
				includeSignature={includeSignature}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				baseKey={baseKey}
				hwcStep={hwcStep}
				templateId={templateId}
				stepId={stepId}
				impersonationContext={impersonationContext}
				onNameChange={handleNameChange}
				onNameBlur={handleNameBlur}
				onSelectTemplate={handleSelectTemplate}
				onContentStateChange={handleContentStateChange}
				onContentBlur={handleContentBlur}
				onIncludeSignatureChange={handleIncludeSignatureChange}
				onClearError={clearInlineErrorMessage}
				onStepChanged={handleStepChanged}
				updateStep={handleUpdateStep}
			/>
			<StepAdvancedOptionsPanel
				isExpanded={advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				stepId={stepId || nodeId}
				filter={skipStepContactFilter}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				onFilterChange={handleFilterChange}
			>
				{hwcStep && (
					<StepSendFromOption
						step={hwcStep}
						onSave={handleSaveSenderOptions}
						connectionTypes={[]}
						sendFromLabel='Always send this step from'
						canEdit={canEdit}
						impersonationContext={impersonationContextViewModel}
					/>
				)}
			</StepAdvancedOptionsPanel>
			<HandwrittenCardTemplateSearchModal
				isOpen={isTemplateModalOpen}
				onClose={() => setIsTemplateModalOpen(false)}
				onTemplateClicked={handleTemplateChosen}
				resourceSelectorId={trigger?.resourceSelector}
			/>
		</div>
	);
}
